import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useUIStore } from '../stores/uiStore';
import { ThemeToggleIconOnly } from '../components/common/theme-toggle';
import { requestPasswordReset } from '../services/api';

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useState(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex h-screen items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="modern-card-gradient overflow-hidden">
            <div className="bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 p-6 md:p-8 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-md rounded-xl p-2 shadow-inner flex-shrink-0">
                    <img src="/logo.png" alt="Logo" className="h-8 w-auto object-contain" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold">Check Your Email</h2>
                </div>
                <ThemeToggleIconOnly />
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-6">
              <div className="text-center py-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-600 mb-6">
                  If an account exists with this email, a password reset link has been sent.
                </p>
                <Link
                  to="/custom/login"
                  className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 overflow-y-auto">
      <div className="w-full max-w-md">
        <div className="modern-card-gradient overflow-hidden">
          <div className="bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 p-6 md:p-8 text-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-md rounded-xl p-2 shadow-inner flex-shrink-0">
                  <img src="/logo.png" alt="Logo" className="h-8 w-auto object-contain" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Forgot Password</h2>
              </div>
              <ThemeToggleIconOnly />
            </div>
            <p className="text-primary-100 text-sm md:text-base">
              Enter your email to receive a password reset link
            </p>
          </div>

          <div className="p-6 md:p-8 space-y-4 md:space-y-6">
            {error && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-danger-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-danger-800">Error</h3>
                    <p className="text-sm text-danger-700 mt-1 break-words">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter your email"
                  disabled={loading}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary btn-ripple group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2 text-base md:text-lg font-semibold">
                  {loading ? (
                    <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  )}
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </span>
              </button>
            </form>

            <div className="pt-4 border-t border-gray-200 text-center">
              <Link
                to="/custom/login"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Back to Login
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-gray-500">
          <p>Secure password reset</p>
        </div>
      </div>
    </div>
  );
}
